import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useConfirmModal from '../useConfirmModal';
import useModals from '../useModals';
import {
    setSaleForm,
    updateSaleField,
    setSaleSaved,
    setSaleToViewLoaded,
    setPendingSaleCount,
    setPendingSaleList,
    setFinalizedSaleList,
    setFilteredSaleList,
    clearFilteredSaleList,
    setIsSubmitting,
    clearFormErrors,
    setFormErrors,
    clearSaleForm,
    setActiveDocumentType,
    setPageSize,
    removePendingSaleById,
    updateSaleStatus,
    addSaleProduct,
    updateSaleProduct,
    deleteSaleProduct,
    setSelectedProduct,
    updateSelectedProductField,
    setShowMultiplePaymentModal,
    setHideMultiplePaymentModal,
    resetSaleFormCompletely,
    updateIndividualPayments,
    clearIndividualPayments,
    setPaymentConditionCode,
    setOpenSalePdfModal,
    setCloseSalePdfModal,
    setOpenPendingSaleModal,
    setClosePendingSaleModal,
    setSaleMeta,
    clearSaleMeta,
    setOpenDtePdfModal,
    setCloseDtePdfModal,
    updateSaleTotalsView,
    setPayments,
    addPaymentItem,
    updatePaymentItem,
    removePaymentItem,
    clearPayments,
    setPendingCashierSales,
    setOpenPendingCashierModal,
    setClosePendingCashierModal,
    setCashierProcessingMode,
    removePendingCashierSaleById,
    setActiveBranchId,
} from '../../store/slices/sale/saleSlice';

import { AppDispatch, RootState } from '../../store/store';

import SaleService from '../../services/sale/saleService';
import { mapSaleFormToDTO } from '../../utils/mappers/sale/mapSaleFormToDTO';
import useApiErrorHandler from '../useApiErrorHandler';
import { SaleMapperHelper } from '../../utils/mappers/sale';
import { SaleMappers } from '../../mappers/sale';
import { useCreditNoteBalance } from './useCreditNoteBalance';
import { DOCUMENT_TYPES } from '../../constants/purchase';
import { useMultiplePayment } from './useMultiplePayment';
import { IndividualPayments, SaleFilter, SaleFormValues, SaleProduct } from '../../types/sale';
import { useCreditNoteSaleLookup } from './useCreditNoteSaleLookup';
import { PaymentItemDTO } from '../../types/sale/dto/PaymentItemDTO';
import { refreshRestaurantAfterSaleFinalized } from '../../features/restaurant/billing/restaurantBillingContext';

export const useSale = () => {
    const { fetchProductBalances } = useCreditNoteBalance();
    const { fetchPayments } = useMultiplePayment();
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const { show: showAlert } = useAlerts();
    const confirm = useConfirmModal();
    const { openModal } = useModals();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { clearCreditNoteSaleLookup } = useCreditNoteSaleLookup();

    const {
        form,
        activeBranchId,
        selectedProduct,
        showMultiplePaymentModal,
        saleSaved,
        saleLoaded,
        saleToViewLoaded,
        formErrors,
        pendingSaleCount,
        finalizedSaleCount,
        pendingSaleList,
        pendingCashierSales,
        finalizedSaleList,
        filteredSaleList,
        paginator,
        pageSize,
        isSubmitting,
        activeDocumentType,
        paymentConditionCode,
        showSalePdfModal,
        showPendingSaleModal,
        showPendingCashierModal,
        showDtePdfModal,
        isCashierProcessingMode,
    } = useSelector((state: RootState) => state.sale);

    // console.log(form.payments);

    /**
  * Genera una nueva venta a partir del formulario proporcionado.
  *
  * 📝 Envía los datos del encabezado de la venta (`formData`) al backend usando un DTO mapeado previamente.
  * ✅ Si la respuesta es exitosa (201), actualiza el estado global con la información de la venta generada,
  *    establece el tipo de documento activo y marca la venta como guardada, mostrando una notificación al usuario.
  * ❌ Si la respuesta no es exitosa, muestra una alerta de error indicando que no se pudo generar la venta.
  * 🔁 Siempre oculta el indicador de carga (`loader`) al finalizar, y gestiona cualquier error inesperado mediante `handle`.
  *
  * @param formData - Objeto `SaleFormValues` con los datos del formulario de encabezado de venta.
  */
    const handlerGenerateSale = async (formData: SaleFormValues) => {
        try {
            setSubmitting(true)
            showLoadingIndicator();

            const dto = mapSaleFormToDTO(formData);
            const response = await SaleService.generate(dto);
            // console.log(response);
            const { type, title, message, data, statusCode } = response;

            if (statusCode === 201) {

                dispatch(setSaleForm(data));
                dispatch(setSaleSaved(true));
                dispatch(setActiveDocumentType(data.documentType));
                dispatch(setActiveBranchId(data.branch.id));
                openModal(type, { title, message, });
            } else {
                showAlert('error', 'Error', 'No se pudo generar la venta.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            setSubmitting(false)
            hideLoadingIndicator();
        }
    };

    /**
     * Actualiza el encabezado de una venta existente.
     *
     * 🔄 Muestra una confirmación antes de continuar.
     * ✅ Si el backend responde con éxito, actualiza el estado global y notifica al usuario.
     * ❌ Si hay error, muestra un modal con la información.
     *
     * @param data - Datos actualizados del encabezado (formulario).
     * @param id - ID de la venta a actualizar.
     */
    const handlerUpdateSaleHeader = async (data: SaleFormValues, id: number) => {
        confirm({
            title: '¿Actualizar encabezado?',
            message: 'Esto cambiará el tipo de documento, sucursal o cliente asociado a la venta. ¿Deseas continuar?',
            icon: 'info',
            confirmText: 'Actualizar',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                try {
                    setSubmitting(true);
                    showLoadingIndicator();

                    const dto = mapSaleFormToDTO(data);
                    const response = await SaleService.updateHeader(id, dto);

                    if (response?.statusCode === 200 && response.data) {
                        const updatedForm = SaleMapperHelper.mapToSaleFormValues(response.data);

                        // 👇 Si es nota de crédito, recuperar balances
                        if (updatedForm.documentType?.id === DOCUMENT_TYPES.CREDIT_NOTE) {
                            await fetchProductBalances(updatedForm.creditNoteId, updatedForm.creditFiscalId);
                        }

                        // 🔄 Actualizar Redux
                        dispatch(setSaleForm(updatedForm));
                        dispatch(setSaleSaved(true));
                        dispatch(setActiveDocumentType(updatedForm.documentType));
                        dispatch(setActiveBranchId(updatedForm.branch.id));


                        // 🔥 Si usa pagos múltiples, obtener resumen desde backend
                        if (updatedForm.paymentMethod?.id === 5 /* ID de múltiple */) {
                            fetchPayments(updatedForm.id!);
                        }

                        openModal(response.type, {
                            title: response.title,
                            message: response.message,
                        });

                    } else {
                        showAlert('error', 'Error', 'No se pudo actualizar la venta.');
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    setSubmitting(false);
                    hideLoadingIndicator();
                }
            },
        });
    };


    /**
     * 🏪 Envía una venta al área de caja.
     *
     * ✅ Cambia el estado a PENDING_CASHIER
     * ✅ NO aplica efectos fiscales
     * ✅ NO descuenta inventario
     * ✅ NO imprime
     * ✅ NO genera DTE
     *
     * El cajero será quien posteriormente finalice la venta.
     */
    const handlerSendToCashier = async (saleId: number) => {
        confirm({
            title: '¿Enviar a caja?',
            message:
                'La venta será enviada al área de caja para procesamiento de cobro.',
            icon: 'info',
            confirmText: 'Enviar',
            cancelText: 'Cancelar',

            onConfirm: async () => {
                try {
                    dispatch(setIsSubmitting(true));

                    showLoadingIndicator();

                    const response =
                        await SaleService.sendToCashier(saleId, form.comments);

                    const {
                        title,
                        message,
                        type,
                        statusCode,
                    } = response;

                    if (statusCode === 200) {

                        openModal(type, {
                            title,
                            message,
                        });

                        // 🧹 Limpia formulario actual
                        dispatch(resetSaleFormCompletely());

                        clearCreditNoteSaleLookup();

                        handlerGetPendingCount();


                    } else {

                        openModal('error', {
                            title: 'Error',
                            message:
                                'No se pudo enviar la venta a caja.',
                        });
                    }

                } catch (error: any) {

                    handle(error);

                } finally {

                    hideLoadingIndicator();

                    dispatch(setIsSubmitting(false));
                }
            },
        });
    };


    /**
     * Finaliza una venta aplicando efectos fiscales y de inventario.
     *
     * ⚠️ Muestra una confirmación antes de continuar.
     * ✅ Si el backend responde con éxito, se redirige al historial de ventas.
     * ❌ Si hay un error, se muestra un modal informativo con el mensaje.
     *
     * @param formData - Datos del formulario de venta (incluye ID y tipo de documento).
     */
    const handlerFinalizeSale = async (formData: any) => {
        confirm({
            title: '¿Finalizar venta?',
            message:
                'Una vez finalizada la venta no se podrá modificar. Esta acción aplicará efectos fiscales e inventario.',
            icon: 'info',
            confirmText: 'Finalizar',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                try {
                    const mapper = SaleMappers[formData.documentType?.code];
                    if (!mapper) {
                        throw new Error('No existe mapper definido para este tipo de documento');
                    }

                    const payload = mapper(formData);

                    dispatch(setIsSubmitting(true));
                    showLoadingIndicator();

                    // console.log(payload);

                    const response = await SaleService.finalize(formData.id, payload);

                    const { title, message, type, statusCode, data } = response;
                    // console.log('SaleFinalize', response);

                    if (statusCode === 200) {
                        sessionStorage.setItem('lastFinalizedSaleId', String(data ?? formData.id));
                        openModal(type, { title, message });

                        // 🧹 Limpia formulario y contadores
                        dispatch(resetSaleFormCompletely());
                        clearCreditNoteSaleLookup();
                        handlerGetPendingCount();
                        if (isCashierProcessingMode) {
                            closePendingCashierModal();
                            removePendingCashierSale(data);
                        }

                        const restaurantRefresh =
                            await refreshRestaurantAfterSaleFinalized(
                                Number(formData.id),
                                dispatch
                            );

                        if (restaurantRefresh) {
                            navigate(
                                `/restaurant/operation/orders?branchId=${restaurantRefresh.context.branchId}`
                            );
                        }

                    } else {
                        openModal(type || 'error', {
                            title: title || 'Error al finalizar',
                            message: message || 'No se pudo finalizar la venta.',
                        });
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                    dispatch(setIsSubmitting(false));
                }
            },
        });
    };


    /**
     * 🔁 Obtiene una venta pendiente desde el backend para continuar su registro.
     *
     * ✅ Si la venta existe, mapea los datos al formulario interno, actualiza el tipo de documento activo persistido
     *    (`activeDocumentType`), y redirige al usuario a la vista de registro.
     * 🔄 Si la venta es una Nota de Crédito, también recupera los balances de productos relacionados.
     * ❌ Si la venta no puede recuperarse o ocurre un error, muestra una alerta o error modal.
     *
     * @param id - ID de la venta pendiente que se desea continuar.
     */
    const handlerGetSaleToContinue = async (id: number) => {
        try {
            showLoadingIndicator();

            const response = await SaleService.getToContinue(id);
            // console.log(response);

            if (response?.statusCode === 200 && response.data) {
                const saleFormValues = SaleMapperHelper.mapToSaleFormValues(response.data);

                // 👇 Si la venta es nota de crédito, procesar balances
                if (saleFormValues.documentType?.id === DOCUMENT_TYPES.CREDIT_NOTE) {
                    await fetchProductBalances(
                        saleFormValues.creditNoteId,
                        saleFormValues.creditFiscalId
                    );
                }

                // 👇 Actualizamos el formulario en Redux o estado global
                handlerSetForm(saleFormValues);
                dispatch(setActiveDocumentType(saleFormValues.documentType));
                dispatch(setActiveBranchId(saleFormValues.branch.id));
                dispatch(updateSaleTotalsView(saleFormValues.totalsView));
                closePendingSaleModal();

                // 🔥 👇 NUEVO: Si la venta recuperada está marcada como pagos múltiples
                if (saleFormValues.paymentMethod?.id === 5 /* id de Multiple */) {
                    // 🔁 Llamamos al backend para obtener resumen y pagos previos
                    fetchPayments(saleFormValues.id!);
                }

                navigate('/sale-register');
                clearFilteredSales();
            } else {
                showAlert('error', 'Error', 'No se pudo recuperar la venta para continuar.');
            }
        } catch (error) {
            handle(error); // Manejo global de errores
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * 📝 Obtiene una venta para edición sin restricción de estado.
     *
     * 🔁 Igual que continue, pero permite cargar cualquier venta.
     */
    const handlerGetSaleForEdit = async (id: number) => {
        try {
            showLoadingIndicator();

            const response = await SaleService.getForEdit(id);

            if (response?.statusCode === 200 && response.data) {
                const saleFormValues = SaleMapperHelper.mapToSaleFormValues(response.data);

                // 👇 Nota de crédito
                if (saleFormValues.documentType?.id === DOCUMENT_TYPES.CREDIT_NOTE) {
                    await fetchProductBalances(
                        saleFormValues.creditNoteId,
                        saleFormValues.creditFiscalId
                    );
                }

                // 🔄 Estado global (igual que continue)
                handlerSetForm(saleFormValues);
                dispatch(setActiveDocumentType(saleFormValues.documentType));
                dispatch(setActiveBranchId(saleFormValues.branch.id));
                dispatch(updateSaleTotalsView(saleFormValues.totalsView));

                // 🔥 pagos múltiples
                if (saleFormValues.paymentMethod?.id === 5) {
                    fetchPayments(saleFormValues.id!);
                }

                navigate('/sale-register');
                clearFilteredSales();

            } else {
                showAlert('error', 'Error', 'No se pudo cargar la venta para edición.');
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
 * 💾 Guarda una venta sin finalizarla.
 *
 * ✅ Recalcula y guarda datos
 * ❌ No limpia formulario
 * ❌ No redirige
 * ❌ No dispara polling
 */
    const handlerSaveSale = async (formData: any) => {
        try {
            const mapper = SaleMappers[formData.documentType?.code];
            if (!mapper) {
                throw new Error('No existe mapper definido para este tipo de documento');
            }

            const payload = mapper(formData);

            dispatch(setIsSubmitting(true));
            showLoadingIndicator();

            const response = await SaleService.save(formData.id, payload);


            const { title, message, type, statusCode } = response;

            if (statusCode === 200) {
                openModal(type, { title, message });

                dispatch(resetSaleFormCompletely());
                clearCreditNoteSaleLookup();
                handlerGetPendingCount();

            } else {
                openModal('error', {
                    title: 'Error al guardar',
                    message: 'No se pudo guardar la venta.',
                });
            }

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
            dispatch(setIsSubmitting(false));
        }
    };


    /**
 * Obtiene una venta por su ID para visualizarla en el modal de detalle.
 *
 * 🔍 Consulta la venta en el backend y actualiza el estado global con la venta cargada.
 * ✅ Si la respuesta es exitosa, se muestra el modal con los datos.
 * ❌ Si ocurre un error, se muestra una alerta y se redirige al historial.
 *
 * @param id - ID de la venta a consultar.
 */
    const handlerViewSaleById = async (id: number) => {
        try {
            showLoadingIndicator();

            // 🔹 Llamada al backend
            const response = await SaleService.getById(id);
            console.log(response);

            if (response?.statusCode === 200 && response.data) {
                // 🔹 Mapeamos la respuesta a los valores del formulario de detalle
                const saleFormValues = SaleMapperHelper.mapToSaleFormValues(response.data);

                // 🔹 Actualizamos el formulario en Redux o estado global
                dispatch(setSaleForm(saleFormValues));
                dispatch(setSaleToViewLoaded(true));
                dispatch(setActiveDocumentType(saleFormValues.documentType));
                dispatch(setActiveBranchId(saleFormValues.branch.id));
            } else {
                showAlert('error', 'Error', 'No se pudo obtener la venta.');
                navigate('/sale-history');
            }
        } catch (error) {
            showAlert('error', 'Error', 'Error inesperado al visualizar la venta.');
            navigate('/sale-history');
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerLoadPendingCashierSale = async (
        id: number
    ) => {

        try {

            showLoadingIndicator();

            const response =
                await SaleService.getForEdit(id);

            if (
                response?.statusCode === 200 &&
                response.data
            ) {

                const saleFormValues =
                    SaleMapperHelper.mapToSaleFormValues(
                        response.data
                    );

                // =====================================================
                // 🔹 Credit Note
                // =====================================================

                if (
                    saleFormValues.documentType?.id ===
                    DOCUMENT_TYPES.CREDIT_NOTE
                ) {

                    await fetchProductBalances(
                        saleFormValues.creditNoteId,
                        saleFormValues.creditFiscalId
                    );
                }

                // =====================================================
                // 🔹 Redux
                // =====================================================

                handlerSetForm(saleFormValues);

                dispatch(
                    setActiveDocumentType(
                        saleFormValues.documentType
                    )
                );

                dispatch(
                    updateSaleTotalsView(
                        saleFormValues.totalsView
                    )
                );

                // =====================================================
                // 🔹 Multiple Payments
                // =====================================================

                if (
                    saleFormValues.paymentMethod?.id === 5
                ) {

                    fetchPayments(
                        saleFormValues.id!
                    );
                }

                dispatch(
                    setCashierProcessingMode(true)
                );

                // =====================================================
                // 🔹 Open Modal
                // =====================================================

                openPendingCashierModal();

            } else {

                showAlert(
                    'error',
                    'Error',
                    'No se pudo cargar la venta.'
                );
            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();
        }
    };



    /**
     * Anula una venta registrada.
     *
     * ⚠️ Muestra confirmación al usuario, ya que la acción es irreversible.
     * 🔁 Actualiza el estado global al cambiar la venta a "CANCELLED" y eliminarla de la lista de pendientes.
     *
     * @param id - ID de la venta a anular.
     * @param reason - Razón de la anulación (opcional).
     */
    const handlerCancelSale = async (id: number, reason: string = '') => {
        confirm({
            title: '¿Anular venta?',
            message: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            confirmText: 'Anular',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const response = await SaleService.cancel(id, reason);

                    if (response?.statusCode === 200) {
                        dispatch(updateSaleStatus({ id, status: 'CANCELED' }));
                        dispatch(removePendingSaleById(id));
                        openModal(response.type, {
                            title: response.title,
                            message: response.message,
                        });
                    } else {
                        showAlert('error', 'Error', 'No se pudo anular la venta.');
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };





    /**
     * Obtiene la lista de ventas finalizadas de una sucursal específica.
     *
     * 🔍 Llama al backend para recuperar todas las ventas con estado `FINALIZED`.
     * ✅ Si la llamada es exitosa, actualiza el estado global (`Redux`) con la lista de ventas finalizadas.
     * ❌ En caso de error, muestra una alerta al usuario.
     *
     * @param branchId - ID de la sucursal (por defecto: `1`, Casa Matriz).
     */
    const handlerGetFinalizedSales = async (branchId: number = 1) => {
        try {
            showLoadingIndicator();
            const response = await SaleService.getFinalizedByBranch(branchId);
            dispatch(setFinalizedSaleList(response));
        } catch (error) {
            showAlert('error', 'Error', 'No se pudieron obtener las ventas finalizadas.');
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Filtra las ventas según criterios proporcionados y actualiza el estado global con los resultados.
     *
     * 🔍 Realiza una solicitud al backend con los filtros aplicados, paginación y ordenamiento.
     * ✅ Si la respuesta es válida, actualiza el estado `filteredSaleList` en Redux.
     * ❌ Si ocurre un error o la respuesta es inválida, muestra una alerta de error.
     *
     * @param filters - Objeto con los filtros aplicados (tipo de documento, cliente, fecha, etc.).
     * @param page - Número de página para la paginación (por defecto: `0`).
     * @param size - Tamaño de la página (por defecto: `10`).
     * @param sort - Criterio de ordenamiento (por defecto: `saleDate,desc`).
     */
    const handlerGetFilteredSales = async (
        filters: SaleFilter,
        page = 0,
        size = 10,
    ) => {
        try {
            showLoadingIndicator();
            const response = await SaleService.getFiltered(filters, page, size);
            // console.log(response);

            if (response?.content) {
                dispatch(setFilteredSaleList(response));
            } else {
                showAlert('error', 'Error', 'No se pudieron obtener las ventas filtradas.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Obtiene el resumen general de todas las ventas pendientes (sin filtrar por sucursal).
     *
     * ✅ Llama al backend a `/api/sales/pending-summary/all`.
     * ✅ Devuelve directamente la lista o lanza alerta si falla.
     */
    const handlerGetAllPendingSummary = async () => {
        try {
            showLoadingIndicator();
            const response = await SaleService.getAllPendingSummary();
            const { statusCode, data } = response;

            if (statusCode === 200) {
                dispatch(setPendingSaleList(data)) // Puedes usarlo donde lo necesites (tabla, modal, etc.)
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el resumen de ventas pendientes.');
                return [];
            }
        } catch (error: any) {
            handle(error);
            return [];
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Obtiene el resumen de ventas pendientes para una sucursal.
     *
     * 🔍 Recupera información resumida (cliente, documento, total).
     * ✅ Útil para mostrar en dashboards o tablas compactas.
     *
     * @param branchId - ID de la sucursal (opcional).
     */
    const handlerGetPendingSummary = async (branchId?: number) => {
        try {
            showLoadingIndicator();
            const response = await SaleService.getPendingSummary(branchId);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setPendingSaleList(data))
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el resumen de ventas pendientes.');
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 💵 Obtiene ventas
     * pendientes de cobro
     * para caja.
     */
    const handlerGetPendingCashierSales = async (
        branchId: number,
        sellerId?: number
    ) => {

        try {

            showLoadingIndicator();

            const response =
                await SaleService.getPendingCashierSales(
                    branchId,
                    sellerId
                );

            // console.log('PendingCashierSales', response);

            const {
                statusCode,
                data,
            } = response;

            if (statusCode === 200) {

                dispatch(
                    setPendingCashierSales(data)
                );

            } else {

                showAlert(
                    'error',
                    'Error',
                    'No se pudieron obtener las ventas pendientes de caja.'
                );
            }

        } catch (error: any) {

            handle(error);

        } finally {

            hideLoadingIndicator();
        }
    };


    /**
     * Consulta la cantidad total de ventas pendientes.
     *
     * ✅ Se utiliza para mostrar badges o métricas generales.
     */
    const handlerGetPendingCount = async () => {
        try {
            showLoadingIndicator();
            const response = await SaleService.getPendingCount();
            if (response?.statusCode === 200) {
                dispatch(setPendingSaleCount(response.data));
            } else {
                showAlert('error', 'Error', 'No se pudo contar las ventas pendientes.');
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Limpia el formulario de venta al abandonar `/sale-register` (sin diálogo).
     * Antes esto también marcaba el correlativo como PENDIENTE; esa lógica se
     * eliminó — el correlativo se gestiona internamente en el backend.
     */
    const handlerResetSaleForm = () => {
        dispatch(resetSaleFormCompletely());
    };

    const handlerResetSaleFormOnly = () => {
        const hasData =
            form?.products?.length > 0 ||
            form?.customer ||
            form?.documentType;

        if (!hasData) return;

        confirm({
            title: '¿Limpiar formulario?',
            message:
                'Se perderán los datos ingresados.\n\n¿Deseas continuar?',
            icon: 'warning',
            confirmText: 'Sí, limpiar',
            cancelText: 'Cancelar',
            onConfirm: () => {
                dispatch(resetSaleFormCompletely());
            },
        });
    };



    const handlerApplyGlobalDiscount = async (
        saleId: number,
        percentage: number,
        confirmReplaceManualDiscounts: boolean = false,
        target: 'PRODUCTS' | 'TOTAL' = 'PRODUCTS'
    ) => {

        // 🔒 La condición de pago "Otro" es solo local hasta finalizar (no
        // se persiste en el backend al elegirla). handlerGetSaleForEdit trae
        // la venta desde el servidor y pisa form.paymentCondition con lo ya
        // guardado (Contado), borrando la selección del cajero. La
        // recordamos para restaurarla después del refresco.
        const paymentConditionBeforeRefresh = form.paymentCondition;

        try {

            showLoadingIndicator();

            const response =
                await SaleService.applyGlobalDiscount(
                    saleId,
                    percentage,
                    confirmReplaceManualDiscounts,
                    target
                );

            const {
                statusCode,
                title,
                message,
                type,
            } = response;

            if (statusCode === 200) {

                openModal(type, {
                    title,
                    message,
                });

                // 🔥 refrescar venta actualizada
                await handlerGetSaleForEdit(saleId);

                // 🔄 Restaurar la condición de pago elegida localmente
                if (paymentConditionBeforeRefresh?.id) {
                    dispatch(updateSaleField({
                        field: 'paymentCondition',
                        value: paymentConditionBeforeRefresh,
                    }));
                }

            } else {

                showAlert(
                    'error',
                    'Error',
                    'No se pudo aplicar descuento global.'
                );
            }

        } catch (error: any) {

            const status =
                error?.response?.data?.statusCode ||
                error?.response?.status;

            // =====================================================
            // 🔹 409: hay descuentos manuales activos — se pide
            // confirmación explícita antes de reemplazarlos.
            // =====================================================

            if (status === 409 && !confirmReplaceManualDiscounts) {

                confirm({

                    title: '¿Aplicar descuento global?',

                    message:
                        error?.response?.data?.message ||
                        'Hay descuentos manuales activos en algunas líneas. Aplicar el descuento global los va a reemplazar.',

                    icon: 'warning',

                    confirmText: 'Sí, reemplazar',

                    cancelText: 'Cancelar',

                    onConfirm: () =>
                        handlerApplyGlobalDiscount(
                            saleId,
                            percentage,
                            true,
                            target
                        ),

                });

                return;

            }

            handle(error);

        } finally {

            hideLoadingIndicator();
        }
    };

    const handlerApplyTip = async (
        saleId: number
    ) => {

        try {

            showLoadingIndicator();

            const response =
                await SaleService.applyTip(saleId);

            const {
                statusCode,
            } = response;

            if (statusCode === 200) {

                await handlerGetSaleForEdit(saleId);

            } else {

                showAlert(
                    'error',
                    'Error',
                    'No se pudo aplicar la propina.'
                );
            }

        } catch (error: any) {

            handle(error);

        } finally {

            hideLoadingIndicator();
        }
    };

    const handlerRemoveTip = async (
        saleId: number
    ) => {

        try {

            showLoadingIndicator();

            const response =
                await SaleService.removeTip(saleId);

            const {
                statusCode,
            } = response;

            if (statusCode === 200) {

                await handlerGetSaleForEdit(saleId);

            } else {

                showAlert(
                    'error',
                    'Error',
                    'No se pudo quitar la propina.'
                );
            }

        } catch (error: any) {

            handle(error);

        } finally {

            hideLoadingIndicator();
        }
    };

    const handlerRemoveGlobalDiscount = async (
        saleId: number
    ) => {

        // Mismo motivo que en handlerApplyGlobalDiscount: preservar la
        // condición de pago elegida localmente ("Otro") tras el refresco.
        const paymentConditionBeforeRefresh = form.paymentCondition;

        try {

            showLoadingIndicator();

            const response =
                await SaleService.removeGlobalDiscount(saleId);

            const {
                statusCode,
                title,
                message,
                type,
            } = response;

            if (statusCode === 200) {

                openModal(type, {
                    title,
                    message,
                });

                await handlerGetSaleForEdit(saleId);

                if (paymentConditionBeforeRefresh?.id) {
                    dispatch(updateSaleField({
                        field: 'paymentCondition',
                        value: paymentConditionBeforeRefresh,
                    }));
                }

            } else {

                showAlert(
                    'error',
                    'Error',
                    'No se pudo eliminar el descuento global.'
                );
            }

        } catch (error: any) {

            handle(error);

        } finally {

            hideLoadingIndicator();
        }
    };



    /**
     * Actualiza el tamaño de página para la tabla de ventas.
     *
     * ⚙️ Cambia el valor almacenado en el estado global Redux.
     *
     * @param size - Cantidad de elementos por página.
     */
    const updatePageSize = (size: number) => {
        dispatch(setPageSize(size));
    };

    /**
     * Limpia la lista de ventas filtradas del estado global.
     *
     * 🧹 Útil al reiniciar filtros o al salir del historial de ventas.
     */
    const clearFilteredSales = () => {
        dispatch(clearFilteredSaleList());
    };

    /**
     * Cierra el modal de visualización de ventas.
     *
     * ❌ Oculta el modal y limpia el formulario cargado de la venta.
     */
    const handleCloseViewModal = () => {
        dispatch(setSaleToViewLoaded(false));
        dispatch(clearSaleForm());
    };

    /**
     * ➕ Agrega un nuevo producto a la venta.
     *
     * @param product - Objeto `SaleProduct` que contiene ID, nombre, cantidad, precio, descuento y total.
     *
     * ✅ Si ya existe un producto con el mismo `productId`, será reemplazado.
     * ✅ Si no existe, se agrega al final de la lista.
     *
     * Este método se usa cuando:
     * - Se selecciona un producto desde un catálogo o buscador.
     * - Se calculan los totales y se desea agregar a la venta.
     */
    const handlerAddProduct = (product: SaleProduct) => {
        dispatch(addSaleProduct(product));
    };

    /**
     * 🔄 Actualiza un producto existente en la venta.
     *
     * @param product - Objeto `SaleProduct` con los datos actualizados.
     *
     * ✅ Busca el producto en la lista por `productId` y lo reemplaza.
     * ⚠️ Si no se encuentra, no hace nada.
     *
     * Este método se usa cuando:
     * - Se edita un producto desde un modal o sección de edición.
     */
    const handlerUpdateProduct = (product: SaleProduct) => {
        dispatch(updateSaleProduct(product));
    };

    /**
     * 🗑️ Elimina un producto del formulario de venta.
     *
     * @param productId - ID numérico del producto que se desea eliminar.
     *
     * ✅ Filtra la lista y elimina el producto que coincide con el ID.
     *
     * Este método se usa cuando:
     * - El usuario decide quitar un producto de la venta.
     */
    const handlerDeleteProduct = (productId: number) => {
        dispatch(deleteSaleProduct(productId));
    };

    /**
     * 🧠 Reemplaza por completo el formulario de venta.
     *
     * @param formData - Objeto `SaleFormValues` que contiene encabezado y lista de productos.
     *
     * ✅ Sobrescribe el estado actual con los datos proporcionados.
     * ✅ Limpia errores antiguos (`formErrors`).
     *
     * Este método se usa cuando:
     * - Se genera una venta y se desea reflejar el resultado en el formulario.
     * - Se carga una venta existente para editar o visualizar.
     */
    const handlerSetForm = (formData: SaleFormValues) => {
        dispatch(setSaleForm(formData));
    };

    /**
     * 🎯 Establece el producto actualmente seleccionado para edición o vista.
     *
     * @param product Producto a seleccionar (o `null` para limpiar).
     */
    const handlerSelectProduct = (product: SaleProduct | null) => {
        dispatch(setSelectedProduct(product));
    };

    /**
     * ✏️ Actualiza campos del producto seleccionado en el estado.
     *
     * @param updates Objeto parcial con los campos del producto a modificar.
     */
    const handlerUpdateSelectedProductField = (updates: Partial<SaleProduct>) => {
        dispatch(updateSelectedProductField(updates));
    };

    /**
     * 📂 Abre el modal de pagos múltiples.
     *
     * ✅ Este método despacha la acción `setShowMultiplePaymentModal` del slice de ventas,
     *    cambiando el estado `showMultiplePaymentModal` a `true`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario hace clic en el botón `+ Pagos` dentro de la sección
     *      de la condición de pago "Otro".
     *    - Permite mostrar el componente <MultiplePaymentModal /> en pantalla.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { openMultiPaymentModal } = useSale();
     * <button onClick={openMultiPaymentModal}>+ Pagos</button>
     * ```
     */
    const openMultiPaymentModal = () => {
        dispatch(setShowMultiplePaymentModal());
    };

    /**
     * ❌ Cierra el modal de pagos múltiples.
     *
     * ✅ Este método despacha la acción `setHideMultiplePaymentModal` del slice de ventas,
     *    cambiando el estado `showMultiplePaymentModal` a `false`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario hace clic en el botón `Cerrar` dentro del modal.
     *    - Cuando deseas ocultar el componente <MultiplePaymentModal /> después de guardar o cancelar.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { closeMultiPaymentModal } = useSale();
     * <MultiplePaymentModal isOpen={showMultiplePaymentModal} onClose={closeMultiPaymentModal} />
     * ```
     */
    const closeMultiPaymentModal = () => {
        dispatch(setHideMultiplePaymentModal());
    };


    /**
     * 📄 Abre el modal de visualización de documento PDF o ticket de una venta.
     *
     * ✅ Este método despacha la acción `openSalePdfModal` del slice de ventas,
     *    cambiando el estado `showSalePdfModal` a `true`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario hace clic en un botón como "Ver documento" o "Vista previa".
     *    - Cuando se desea mostrar el componente `<SalePdfModal />` o similar.
     *    - Permite seleccionar entre visualizar como PDF o como Ticket dentro del modal.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { openSalePdfModal } = useSale();
     * <button onClick={openSalePdfModal}>Ver Documento</button>
     * ```
     */
    const openSalePdfModal = () => {
        dispatch(setOpenSalePdfModal());
    };

    /**
     * ❌ Cierra el modal de visualización de documento PDF o ticket.
     *
     * ✅ Este método despacha la acción `closeSalePdfModal` del slice de ventas,
     *    cambiando el estado `showSalePdfModal` a `false`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario hace clic en el botón "Cerrar" o cierra el modal.
     *    - Cuando se desea ocultar el componente `<SalePdfModal />` luego de visualizar el documento.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { closeSalePdfModal } = useSale();
     * <SalePdfModal isOpen={showSalePdfModal} onClose={closeSalePdfModal} />
     * ```
     */
    const closeSalePdfModal = () => {
        dispatch(setCloseSalePdfModal());
    };

    /**
     * 🕓 Abre el modal de ventas pendientes.
     *
     * ✅ Este método despacha la acción `setOpenPendingSaleModal` del slice de ventas,
     *    cambiando el estado `showPendingSaleModal` a `true`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario hace clic en el botón "Ventas pendientes" en la vista de historial.
     *    - Cuando se desea mostrar el modal `<PendingSaleModal />` con el resumen de ventas aún no finalizadas.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { openPendingSaleModal } = useSale();
     * <button onClick={openPendingSaleModal}>Ventas pendientes</button>
     * ```
     */
    const openPendingSaleModal = () => {
        dispatch(setOpenPendingSaleModal());
    };

    /**
     * ❌ Cierra el modal de ventas pendientes.
     *
     * ✅ Este método despacha la acción `setClosePendingSaleModal` del slice de ventas,
     *    cambiando el estado `showPendingSaleModal` a `false`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario cierra manualmente el modal de ventas pendientes.
     *    - Cuando se desea ocultar el componente `<PendingSaleModal />` tras finalizar o cancelar una venta.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { closePendingSaleModal } = useSale();
     * <PendingSaleModal isOpen={showPendingSaleModal} onClose={closePendingSaleModal} />
     * ```
     */
    const closePendingSaleModal = () => {
        dispatch(setClosePendingSaleModal());
    };

    /**
     * 📄 Abre el modal exclusivo de visualización del DTE en PDF.
     *
     * ✅ Este método despacha la acción `setOpenDtePdfModal` del slice de ventas,
     *    cambiando el estado `showDtePdfModal` a `true`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario desea ver la versión oficial del DTE emitido en formato PDF.
     *    - Cuando se desea mostrar el componente `<DtePdfModal />` para una visualización directa sin opción a cambio de tipo.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { openDtePdfModal } = useSale();
     * <button onClick={openDtePdfModal}>Ver DTE PDF</button>
     * ```
     */
    const openDtePdfModal = () => {
        dispatch(setOpenDtePdfModal());
    };

    /**
     * ❌ Cierra el modal exclusivo de visualización de DTE en PDF.
     *
     * ✅ Este método despacha la acción `setCloseDtePdfModal` del slice de ventas,
     *    cambiando el estado `showDtePdfModal` a `false`.
     *
     * 🔹 Cuándo usarlo:
     *    - Cuando el usuario cierra manualmente el modal de DTE PDF.
     *    - Al finalizar la visualización del documento.
     *
     * 🛠️ Ejemplo de uso en un componente:
     * ```tsx
     * const { closeDtePdfModal } = useSale();
     * <DtePdfModal isOpen={showDtePdfModal} onClose={closeDtePdfModal} />
     * ```
     */
    const closeDtePdfModal = () => {
        dispatch(setCloseDtePdfModal());
    };


    /**
 * 💵 Abre modal
 * de ventas pendientes
 * de cobro.
 */
    const openPendingCashierModal = () => {
        dispatch(setOpenPendingCashierModal());
    };

    /**
     * ❌ Cierra modal
     * de ventas pendientes
     * de cobro.
     */
    const closePendingCashierModal = () => {
        dispatch(setCashierProcessingMode(false));
        dispatch(setClosePendingCashierModal());
    };


    // ✅ Esta función centraliza el seteo de id y sucursal para acciones PDF, DTE, etc.
    const setSaleMetaData = (id: number, branchId: number) => {
        dispatch(setSaleMeta({ id, branchId }));
    };

    const clearSaleMetaData = () => {
        dispatch(clearSaleMeta());
    };

    const setSubmitting = (value: boolean) => {
        dispatch(setIsSubmitting(value));
    };


    const setPaymentConditionCodeFn = (code: string | null) => {
        dispatch(setPaymentConditionCode(code));
    };

    const setAllPayments = (payments: PaymentItemDTO[]) => {
        dispatch(setPayments(payments));
    };


    const addPayment = (payment: PaymentItemDTO) => {
        dispatch(addPaymentItem(payment));
    };

    const updatePayment = (index: number, item: PaymentItemDTO) => {
        dispatch(updatePaymentItem({ index, item }));
    };

    const removePayment = (index: number) => {
        dispatch(removePaymentItem(index));
    };

    const clearAllPayments = () => {
        dispatch(clearPayments());
    };

    const removePendingCashierSale = (saleId: number) => {
        dispatch(removePendingCashierSaleById(saleId));
    };


    const handlerResetSaleWorkspace = async () => {
        dispatch(resetSaleFormCompletely());

        clearCreditNoteSaleLookup();

        await handlerGetPendingCount();
    };

    return {
        form,
        activeBranchId,
        selectedProduct,
        showMultiplePaymentModal,
        saleSaved,
        saleLoaded,
        saleToViewLoaded,
        formErrors,
        filteredSaleList,
        pendingSaleList,
        finalizedSaleList,
        pendingSaleCount,
        finalizedSaleCount,
        paginator,
        pageSize,
        isSubmitting,
        activeDocumentType,

        setActiveBranchId: (
            branchId: number | null
        ) => {
            dispatch(setActiveBranchId(branchId));
        },

        updateField: <K extends keyof SaleFormValues>(
            field: K,
            value: SaleFormValues[K]
        ) => dispatch(updateSaleField({ field, value })),

        updateIndividualPayments: (updates: Partial<IndividualPayments>) =>
            dispatch(updateIndividualPayments(updates)),

        clearIndividualPayments: () => dispatch(clearIndividualPayments()),

        clearForm: () => dispatch(clearSaleForm()),
        setErrors: (errors: Record<string, string>) => dispatch(setFormErrors(errors)),
        clearErrors: () => dispatch(clearFormErrors()),

        setSubmitting,
        handlerAddProduct,
        handlerUpdateProduct,
        handlerDeleteProduct,
        handlerSetForm,
        setSaleMetaData,
        clearSaleMetaData,

        handlerGenerateSale,
        handlerUpdateSaleHeader,
        handlerSendToCashier,
        handlerFinalizeSale,
        handlerGetSaleForEdit,
        handlerSaveSale,
        handlerCancelSale,
        handlerViewSaleById,
        handlerLoadPendingCashierSale,
        handlerGetFinalizedSales,
        handlerGetFilteredSales,
        handlerSelectProduct,
        handlerUpdateSelectedProductField,
        openMultiPaymentModal,
        closeMultiPaymentModal,

        updatePageSize,
        clearFilteredSales,
        handleCloseViewModal,

        handlerGetPendingCount,
        handlerGetAllPendingSummary,
        handlerGetPendingSummary,
        handlerGetSaleToContinue,
        handlerResetSaleForm,
        handlerResetSaleFormOnly,

        paymentConditionCode,
        setPaymentConditionCodeFn,

        openSalePdfModal,
        closeSalePdfModal,
        showSalePdfModal,

        openPendingSaleModal,
        closePendingSaleModal,
        showPendingSaleModal,


        openDtePdfModal,
        closeDtePdfModal,
        showDtePdfModal,

        setAllPayments,
        addPayment,
        updatePayment,
        removePayment,
        clearAllPayments,
        removePendingCashierSale,

        handlerApplyGlobalDiscount,
        handlerRemoveGlobalDiscount,
        handlerApplyTip,
        handlerRemoveTip,

        pendingCashierSales,
        showPendingCashierModal,

        handlerGetPendingCashierSales,

        openPendingCashierModal,
        closePendingCashierModal,
        isCashierProcessingMode,
        handlerResetSaleWorkspace
    };
};
